/**
 * Shared API client utilities for AI Core UI
 *
 * Mirrors warehouse-ui's client.ts — uses the same apiFetch wrapper
 * from @lognai/plugin-sdk via getApiHeaders, and helpers from
 * @lognai/frontend-core.
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { buildQueryString, buildPaginationQuery } from '@lognai/frontend-core';

export { buildQueryString, buildPaginationQuery };
export type { ListQueryParams } from '@lognai/frontend-core';
export type { PaginationParams } from '@lognai/plugin-sdk';

/**
 * The company a platform admin's chat is running for, or null.
 *
 * A platform admin has no company, and the chat is company-scoped, so the shell
 * names the company the chat was opened for (the one picked on the page) and
 * every AI request carries it. The server honours the header for platform
 * admins only. Module state, like the session snapshot the headers already come
 * from: the shell sets it before it opens the chat and clears it on close.
 */
let actingCompanyId: string | null = null;

export const setActingCompany = (companyId: string | null): void => {
  actingCompanyId = companyId && companyId !== '' ? companyId : null;
};

export const getActingCompany = (): string | null => actingCompanyId;

export const actingCompanyHeaders = (): Record<string, string> =>
  actingCompanyId ? { 'X-Act-For-Company': actingCompanyId } : {};

/**
 * Standardized fetch wrapper with error handling
 */
export const apiFetch = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...actingCompanyHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized', status: 401 });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, { ...error.details, status: response.status });
  }

  // A 204 carries no body by definition, and `response.json()` on an empty one
  // throws "Unexpected end of JSON input" — which surfaced a SUCCESSFUL delete
  // as a red error toast (QA ledger PROD-001). Every plugin's client had the
  // same unguarded call.
  //
  // Deliberately narrow: only the statuses that are DEFINED to have no body are
  // special-cased. Reading every response as text first would also cover a 200
  // with an empty body, but that is speculative, and it changes the parse path
  // for every call in the product to fix a case nobody has hit.
  if (response.status === 204 || response.status === 205) return undefined as T;

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', { ...(apiResponse as any).details, status: response.status });
  }

  return apiResponse.data;
};
